/**
 * Published rate-card client (D0d — canonical `rate_cards` store).
 *
 * The platform publishes a per-matter-type / jurisdiction / county ANCHOR that
 * guides — never gates — the attorney's proposed legal fee (MASTER_DESIGN_PLAN
 * §2 locked decision #3: attorney-proposes, platform-anchors). The anchor is a
 * BAND (low/high legal fee) plus a separate FLAT platform software fee. This is
 * the read half of the frozen public contract:
 *
 *   GET /rate-card → { ok, anchors: [{ matterType, jurisdiction, county,
 *     anchorLegalFeeLowCents, anchorLegalFeeHighCents, platformFeeCents,
 *     currency, scope, slaHours, effectiveFrom }] }   // published rows only
 *
 * The endpoint may not exist yet in a given environment (the API is a sibling
 * repo shipping in parallel); every caller degrades gracefully to "no anchor".
 */
import { authHeader } from './session';

export interface RateCardAnchor {
  matterType: string;
  jurisdiction: string;
  county: string | null;
  anchorLegalFeeLowCents: number;
  anchorLegalFeeHighCents: number;
  /** Flat platform software fee — a SEPARATE line, never a % of the legal fee. */
  platformFeeCents: number;
  currency: string;
  scope: string | null;
  slaHours: number | null;
  effectiveFrom: string | null;
}

export type RateCardResult =
  | { ok: true; anchors: RateCardAnchor[] }
  | { ok: false; error: string };

export async function fetchRateCard(): Promise<RateCardResult> {
  try {
    const res = await fetch('/api/rate-card', {
      headers: { Accept: 'application/json', ...authHeader() },
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok || body.ok === false) return { ok: false, error: body.error ?? `HTTP ${res.status}` };
    return { ok: true, anchors: (body.anchors ?? []) as RateCardAnchor[] };
  } catch (err) {
    return { ok: false, error: (err as Error).message };
  }
}

/**
 * Pick the best-matching published anchor for a matter. Prefers an exact
 * matter-type + jurisdiction match, then matter-type alone, else none. Never
 * throws; returns null when nothing matches (the surfaces then hide the band).
 */
export function pickAnchor(
  anchors: RateCardAnchor[],
  matterType: string | undefined,
  jurisdiction: string | undefined,
): RateCardAnchor | null {
  if (!matterType) return null;
  const mt = matterType.toLowerCase();
  const jur = jurisdiction?.trim().toLowerCase();
  const sameType = anchors.filter((a) => a.matterType?.toLowerCase() === mt);
  if (sameType.length === 0) return null;
  if (jur) {
    const exact = sameType.find((a) => a.jurisdiction?.trim().toLowerCase() === jur);
    if (exact) return exact;
  }
  return sameType[0];
}

/** Where an entered legal fee sits relative to an anchor band. */
export type BandPosition = 'below' | 'within' | 'above';

export function bandPosition(cents: number, anchor: RateCardAnchor): BandPosition {
  if (cents < anchor.anchorLegalFeeLowCents) return 'below';
  if (cents > anchor.anchorLegalFeeHighCents) return 'above';
  return 'within';
}
